/**
 * New Relic attributes that tell React and legacy questions apart (RPI-1138).
 *
 * Each widget instance carries `_renderPath`: `react` (set by the bridge),
 * `legacy` (`Widget.fromObject`'s legacy widget) or `react-recovered` (rebuilt
 * as legacy after a React crash on mount, in survey.js). No React import: the
 * main bundle (report.js, question.js, Base) uses this module.
 */
export type RenderPath = 'react' | 'legacy' | 'react-recovered';

export interface TelemetryQuestion {
  type?: string;
  view?: string;
  element_id?: string;
}

interface TaggedWidget {
  _object?: TelemetryQuestion;
  _renderPath?: RenderPath;
  _destroyed?: boolean;
}

/** Keeps `renderPaths` within New Relic's attribute size on very long pages. */
const MAX_RENDER_PATHS = 20;

/** The survey the respondent is in: the `segments` path. */
const survey = (): string => window.segments || '';

/** The page's widgets in page order, without a crashed bridge (destroyed). */
const pageWidgets = (): TaggedWidget[] => {
  const widgets = window.Gryphon?.widgets as
    | { instances?: { toArray?(): TaggedWidget[] } }
    | undefined;
  return (widgets?.instances?.toArray?.() || []).filter(
    (widget) => !widget._destroyed
  );
};

/** One question's attributes; a field the question does not have is left out. */
export const questionAttributes = (
  question: TelemetryQuestion = {},
  renderPath?: RenderPath
): Record<string, string> => {
  const attributes: Record<string, string> = { survey: survey() };
  const fields: [string, string | undefined][] = [
    ['renderPath', renderPath],
    ['questionType', question.type],
    ['questionView', question.view],
    ['elementId', question.element_id],
  ];
  fields.forEach(([name, value]) => {
    if (value) {
      attributes[name] = value;
    }
  });
  return attributes;
};

/** The page's widgets as `elementId:type:path`, joined by `;`, in page order. */
export const pageRenderPaths = (): string =>
  pageWidgets()
    .slice(0, MAX_RENDER_PATHS)
    .map((widget) =>
      [
        widget._object?.element_id,
        widget._object?.type,
        widget._renderPath,
      ].join(':')
    )
    .join(';');

/**
 * The current page for the `Next` page action, taken before the page is torn
 * down. `loadMs` (first chunk download to last render) is left out when the
 * page loaded no widgets.
 */
export const pageSnapshot = (): Record<string, string | number> => {
  const widgets = pageWidgets();
  const count = (path: RenderPath): number =>
    widgets.filter((widget) => widget._renderPath === path).length;
  const snapshot: Record<string, string | number> = {
    renderPaths: pageRenderPaths(),
    reactCount: count('react'),
    legacyCount: count('legacy'),
    recoveredCount: count('react-recovered'),
    survey: survey(),
  };
  const loadMs = window.page_state?.widgets_load_ms;
  if (typeof loadMs === 'number') {
    snapshot.loadMs = loadMs;
  }
  return snapshot;
};

interface LoaderError extends Error {
  cause?: unknown;
  telemetryAttributes?: Record<string, string>;
}

/**
 * Labels a page-loader failure for New Relic: `widgetLoadError` (named
 * `WidgetLoadError`) before render and in the page tail, `customQSLError` at
 * the customer boundaries. A `ChunkLoadError`, or an error already labelled
 * deeper, is returned as it is; the question's attributes ride on the error.
 */
export const tagLoaderError = (
  err: unknown,
  cause: 'widgetLoadError' | 'customQSLError',
  question?: TelemetryQuestion,
  renderPath?: RenderPath
): Error => {
  const error: LoaderError =
    err instanceof Error ? err : new Error(String(err));
  if (
    error.name === 'ChunkLoadError' ||
    error.cause === 'widgetLoadError' ||
    error.cause === 'customQSLError'
  ) {
    return error;
  }
  try {
    error.cause = cause;
    error.telemetryAttributes =
      error.telemetryAttributes || questionAttributes(question, renderPath);
    if (cause === 'widgetLoadError') {
      error.name = 'WidgetLoadError';
    }
  } catch {
    /* a frozen error, or a DOMException's read-only name, keeps what it has */
  }
  return error;
};
